import { useEffect, useRef, useState } from 'react'
import { api } from '../lib/api'
import { IS_FIREBASE_ENABLED, auth } from '../lib/firebase'
import { getAnonymousSession, setAnonymousSession } from '../lib/auth'
import type { Student } from './useAuth'

interface AnonymousSessionResponse {
  token: string
  student_id: string
  student?: Student
}

/**
 * Anonymous-first onboarding bootstrap.
 *
 * Narrowly scoped: only call from learning-entry routes that should
 * create an anonymous Student row when the browser has no Firebase
 * user and no existing anonymous token.
 *
 * StrictMode-safe: the request is not cancelled on effect cleanup,
 * because doing so was racing with the second mount and dropping the
 * 201 response on the floor. The bootstrap shares state via
 * localStorage + the useAuth refresh callback, so letting an in-flight
 * promise complete after a fake unmount is correct: the next mount
 * picks up the freshly-stored session and skips re-firing.
 */
export function useAnonymousBootstrap(enabled: boolean, refresh: () => Promise<unknown>) {
  const shouldAttempt =
    enabled &&
    IS_FIREBASE_ENABLED &&
    !auth?.currentUser &&
    !getAnonymousSession()

  const [bootstrapping, setBootstrapping] = useState(shouldAttempt)
  // Single-attempt guard. Without this the StrictMode double-invoke would
  // either (a) fire two requests, hitting the IP rate-limit, or (b) the
  // second mount would prematurely setBootstrapping(false) while the first
  // request was still in flight, letting the route guard bounce to /login
  // before refresh() landed.
  const inFlightRef = useRef(false)

  useEffect(() => {
    if (!shouldAttempt) {
      setBootstrapping(false)
      return
    }
    if (inFlightRef.current) {
      // Another mount has already started the bootstrap. Don't fire a
      // second request and don't change bootstrapping — the original
      // promise will resolve and flip the flag.
      return
    }
    inFlightRef.current = true
    setBootstrapping(true)

    void (async () => {
      try {
        const data = await api.post<AnonymousSessionResponse>('/auth/anonymous-session')
        setAnonymousSession({ token: data.token, student_id: data.student_id })
        await refresh()
      } catch {
        // Leave the page in the unauthenticated state. Route guards
        // fall back to /login if no session was acquired.
      } finally {
        inFlightRef.current = false
        setBootstrapping(false)
      }
    })()
  }, [refresh, shouldAttempt])

  return bootstrapping
}
